import { useGetAllCategories, useGetAllMembershipBenefits } from '@/utils/api/membership-service'
import type { CreateMembershipBenefitDTO } from '@/utils/api/membership-service/models'
import { mapApiListResponse } from '@/utils/api/response-helpers'

export type BenefitAwardMode = 'AUTOMATIC' | 'MANUAL_SELECTION'

export interface BenefitCategory {
  id: string
  name: string
}

export interface BenefitType {
  id: string
  title: string
  description: string
  categoryIds: string[]
  categories: BenefitCategory[]
  pointValue: number
  awardMode: BenefitAwardMode
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateBenefitInput {
  title: string
  description: string
  categoryIds: string[]
  pointValue: number
  awardMode: BenefitAwardMode
  isActive: boolean
}

export const AWARD_MODE_LABELS: Record<BenefitAwardMode, string> = {
  AUTOMATIC: 'Automatic',
  MANUAL_SELECTION: 'Manual selection',
}

const AWARD_MODES = Object.keys(AWARD_MODE_LABELS) as BenefitAwardMode[]

function normaliseAwardMode(value: unknown): BenefitAwardMode {
  if (typeof value === 'string') {
    const normalised = value.trim().toUpperCase().replaceAll('-', '_').replaceAll(' ', '_')
    if (AWARD_MODES.includes(normalised as BenefitAwardMode)) {
      return normalised as BenefitAwardMode
    }
  }

  return 'MANUAL_SELECTION'
}

function normalisePointValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }

  return 0
}

function normaliseBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return true
}

function normaliseDate(value: unknown): string {
  if (typeof value === 'string' && !Number.isNaN(Date.parse(value))) return value
  return new Date().toISOString()
}

function normaliseCategoryId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString()
  if (typeof value === 'string' && value.trim()) return value.trim()
  return null
}

function normaliseCategoryIds(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map(normaliseCategoryId).filter((id): id is string => Boolean(id))
}

function mapCategoryFromApi(category: unknown): BenefitCategory | null {
  if (!category || typeof category !== 'object') return null

  const categoryRecord = category as Record<string, unknown>
  const id = normaliseCategoryId(categoryRecord.id)
  const name =
    typeof categoryRecord.categoryName === 'string'
      ? categoryRecord.categoryName
      : typeof categoryRecord.name === 'string'
        ? categoryRecord.name
        : ''

  if (!id || !name) return null
  return { id, name }
}

function normaliseCategories(value: unknown): BenefitCategory[] {
  if (!Array.isArray(value)) return []
  return value.map(mapCategoryFromApi).filter((category): category is BenefitCategory => Boolean(category))
}

function mapBenefitFromApi(benefit: unknown): BenefitType | null {
  if (!benefit || typeof benefit !== 'object') return null

  const benefitRecord = benefit as Record<string, unknown>
  const idCandidate = benefitRecord.id

  if (typeof idCandidate !== 'string' && typeof idCandidate !== 'number') {
    return null
  }

  const title =
    typeof benefitRecord.benefitTitle === 'string'
      ? benefitRecord.benefitTitle
      : typeof benefitRecord.title === 'string'
        ? benefitRecord.title
        : 'Untitled Benefit'

  const description =
    typeof benefitRecord.benefitDescription === 'string'
      ? benefitRecord.benefitDescription
      : typeof benefitRecord.description === 'string'
        ? benefitRecord.description
        : ''

  const createdAt = normaliseDate(benefitRecord.createdAt)
  const categories = normaliseCategories(benefitRecord.categories)
  const categoryIds = categories.length > 0 ? categories.map((category) => category.id) : normaliseCategoryIds(benefitRecord.categoryIds)

  return {
    id: idCandidate.toString(),
    title,
    description,
    categoryIds,
    categories,
    pointValue: normalisePointValue(benefitRecord.pointValue ?? benefitRecord.points),
    awardMode: normaliseAwardMode(benefitRecord.awardMode),
    isActive: normaliseBoolean(benefitRecord.active ?? benefitRecord.isActive),
    createdAt,
    updatedAt: normaliseDate(benefitRecord.updatedAt ?? createdAt),
  }
}

function mapBenefitsResponse(payload: unknown): BenefitType[] {
  return mapApiListResponse(payload, (benefit) => mapBenefitFromApi(benefit)).filter(
    (benefit): benefit is BenefitType => Boolean(benefit),
  )
}

export function useBenefitsConfig() {
  return useGetAllMembershipBenefits<BenefitType[]>({
    query: {
      select: (response) => mapBenefitsResponse(response.data),
      staleTime: 5 * 60 * 1000,
    },
  })
}

export function useBenefitCategories() {
  return useGetAllCategories<BenefitCategory[]>({
    query: {
      select: (response) =>
        mapApiListResponse(response.data, (category) => mapCategoryFromApi(category)).filter(
          (category): category is BenefitCategory => Boolean(category),
        ),
      staleTime: 5 * 60 * 1000,
    },
  })
}

export function toMembershipBenefitPayload(
  input: CreateBenefitInput,
): CreateMembershipBenefitDTO & Record<string, unknown> {
  return {
    benefitTitle: input.title,
    benefitDescription: input.description,
    categoryIds: input.categoryIds.map(Number).filter(Number.isFinite),
    pointValue: input.pointValue,
    awardMode: input.awardMode,
    active: input.isActive,
  }
}
